// Framework-agnostic refcounted subscription registry backing
// LiveEventsContext.tsx — split out (mirroring how live-socket.ts separates
// the transport from the React wrapper) so its linger/refcount/replay
// semantics are unit-testable with plain vitest, without a DOM renderer
// (this project has no jsdom/happy-dom installed — see live-socket.test.ts's
// header comment).
//
// SubscriptionRegistry owns exactly the state LiveEventsContext.tsx used to
// keep in a bare `useRef(new Map())`: one entry per subscribed LiveScope,
// refcounted across every useLiveSubscription(scope) call anywhere in the
// tree, plus the "acked" flag driving useScopeLive. It knows nothing about
// React, WebSockets, or TanStack Query — it takes a `sendFrame` callback for
// wire I/O and exposes plain methods the context calls from its effects and
// its useMemo'd context value.
import type { LiveScope } from '../lib/live-socket'

// UNSUBSCRIBE_LINGER_MS is how long a scope stays registered (refCount at 0,
// wire subscription untouched) after its last consumer unmounts, before the
// actual `unsubscribe` frame is sent. This absorbs the registration churn a
// naive immediate-unsubscribe would produce: React <StrictMode>'s
// mount->unmount->remount cycle, a tab switching components
// mounting/unmounting, or a fast in-app navigation that briefly leaves and
// re-enters the same scope all unsubscribe-then-resubscribe within
// milliseconds. See docs/apis/events.md's "Unsubscribe linger" section.
export const UNSUBSCRIBE_LINGER_MS = 1000

export interface SubscriptionEntry {
    scope: LiveScope
    refCount: number
    acked: boolean
    // Non-null while refCount is 0 and this entry is waiting out the linger
    // before actually unsubscribing. A subscribe() for the same key before
    // this fires cancels it with zero wire traffic and `acked` left exactly
    // as it was — no reset, no blip.
    pendingRemovalTimer: ReturnType<typeof setTimeout> | null
}

// scopeKey turns a LiveScope into a stable Map key. UID absent means the
// collection scope — keep that distinguishable from an empty-string uid
// (which should never occur, but "assets:" vs "assets" would otherwise
// collide with a hypothetical uid of "").
export function scopeKey(scope: LiveScope): string {
    return scope.uid !== undefined ? `${scope.entity}:${scope.uid}` : scope.entity
}

function clearPendingRemoval(entry: SubscriptionEntry) {
    if (entry.pendingRemovalTimer !== null) {
        clearTimeout(entry.pendingRemovalTimer)
        entry.pendingRemovalTimer = null
    }
}

export class SubscriptionRegistry {
    private readonly entries = new Map<string, SubscriptionEntry>()
    // sendFrame is swappable (via setSendFrame) rather than fixed at
    // construction: LiveEventsContext.tsx creates ONE registry per provider
    // instance (persisting across `org` changes, since only the socket
    // connection effect re-runs), but the actual socket handle it should
    // send through is recreated on every (re)connect.
    private sendFrame: (frame: Record<string, unknown>) => void = () => {}

    setSendFrame(sendFrame: (frame: Record<string, unknown>) => void): void {
        this.sendFrame = sendFrame
    }

    /** Increments refcount for `scope` (creating the entry on first use) and sends `subscribe` on the 0->1 transition — or cancels a pending linger with zero wire traffic if one was running. */
    subscribe(scope: LiveScope): void {
        const key = scopeKey(scope)
        const existing = this.entries.get(key)
        if (existing) {
            if (existing.pendingRemovalTimer !== null) {
                // A new consumer arrived while this scope was lingering
                // toward unsubscribe (refCount was 0). Cancel the linger and
                // reinstate it at refCount 1 with ZERO wire traffic —
                // `acked` is left exactly as it was (still true if the
                // server had acked it before), so useScopeLive never blips
                // false for what turned out to be a transient
                // unmount/remount.
                clearPendingRemoval(existing)
                existing.refCount = 1
                return
            }
            existing.refCount += 1
            return
        }
        this.entries.set(key, { scope, refCount: 1, acked: false, pendingRemovalTimer: null })
        this.sendFrame({ type: 'subscribe', entity: scope.entity, uid: scope.uid })
    }

    /** Decrements refcount for `scope`; on reaching 0, starts the linger timer instead of unsubscribing immediately. No-op if `scope` isn't currently registered. */
    unsubscribe(scope: LiveScope): void {
        const key = scopeKey(scope)
        const existing = this.entries.get(key)
        if (!existing) return
        existing.refCount -= 1
        if (existing.refCount > 0) return
        // Don't send the wire `unsubscribe` (or delete the entry) yet —
        // linger in case a new subscribe() for this same scope arrives
        // imminently (see subscribe above). This is what turns
        // mount->unmount->remount / tab-switch / quick-navigation churn into
        // zero wire traffic instead of an unsubscribe+subscribe pair every
        // time.
        existing.pendingRemovalTimer = setTimeout(() => {
            this.entries.delete(key)
            this.sendFrame({ type: 'unsubscribe', entity: scope.entity, uid: scope.uid })
        }, UNSUBSCRIBE_LINGER_MS)
    }

    isScopeAcked(scope: LiveScope): boolean {
        return this.entries.get(scopeKey(scope))?.acked ?? false
    }

    /** Marks the entry matching (entity, uid) as acked, if currently registered. Returns whether it flipped anything (so callers only notify listeners on an actual change). */
    markAcked(entity: string, uid: string | undefined): boolean {
        const entry = this.entries.get(scopeKey({ entity, uid }))
        if (!entry) return false
        entry.acked = true
        return true
    }

    /** Marks every entry as not-acked (called on socket disconnect) — a reconnect's replay() will re-ack via the server's `subscribed` frames. */
    clearAllAcked(): void {
        for (const entry of this.entries.values()) entry.acked = false
    }

    /** All currently registered scopes (used for a `resync` frame's invalidate-everything). */
    allScopes(): LiveScope[] {
        return Array.from(this.entries.values()).map((e) => e.scope)
    }

    /**
     * Called on a fresh connection's `onOpen`. Replays every actively
     * subscribed entry (resets `acked` to false, re-sends `subscribe`) —
     * EXCEPT entries currently lingering toward removal, which are dropped
     * outright instead: the new connection never knew about them, so there
     * is nothing to replay, and resurrecting a subscription whose last
     * consumer already unmounted would defeat the linger's purpose.
     */
    replay(): void {
        for (const [key, entry] of this.entries) {
            if (entry.pendingRemovalTimer !== null) {
                clearPendingRemoval(entry)
                this.entries.delete(key)
                continue
            }
            entry.acked = false
            this.sendFrame({ type: 'subscribe', entity: entry.scope.entity, uid: entry.scope.uid })
        }
    }

    /**
     * Clears every pending-removal linger timer and deletes those entries
     * outright, without sending any wire frame. Called when the connection
     * this registry's sendFrame was bound to is being torn down (provider
     * unmount, or an `org` change tearing down the connection for a fresh
     * one) — leaving a lingering entry in place with its timer cleared
     * would make the NEXT connection's replay() wrongly treat it as
     * still-active and resubscribe a scope whose last consumer already
     * unmounted.
     */
    dropPendingRemovals(): void {
        for (const [key, entry] of this.entries) {
            if (entry.pendingRemovalTimer !== null) {
                clearPendingRemoval(entry)
                this.entries.delete(key)
            }
        }
    }

    /** Test/debug helper: number of currently registered entries (including lingering ones). */
    size(): number {
        return this.entries.size
    }
}
